import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readPresentation, shapesOfType } from "@/office/powerpoint/presentation";
import { hasFilledHeaderRow, readTables, verboseCells } from "@/office/powerpoint/tabellen";
import { nichtPruefbar } from "../schnittstelle";

export const lektionTabellen: Lesson = {
  id: "tabellen",
  title: "Tabellen auf Folien",
  summary: "Eine Folientabelle ist kein Tabellenblatt – sie zeigt, was schon feststeht.",
  durationMinutes: 10,
  learningGoals: [
    "Tabellen über den Inhaltsplatzhalter einfügen",
    "Den Umfang einer Folientabelle sinnvoll begrenzen",
    "Eine lesbare Kopfzeile anlegen",
  ],
  steps: [
    {
      id: "wozu-tabellen",
      title: "Wofür eine Tabelle auf der Folie da ist",
      explanation: [
        "Eine Tabelle in Excel ist ein Arbeitsmittel: Man sucht darin, sortiert, rechnet. " +
          "Eine Tabelle auf einer Folie ist etwas anderes – sie zeigt ein fertiges " +
          "Ergebnis, das man in wenigen Sekunden erfassen soll.",
        "Daraus folgt eine harte Grenze beim Umfang. Mehr als etwa fünf Zeilen und vier " +
          "Spalten kann niemand im Vorbeigehen lesen, während gleichzeitig jemand " +
          "spricht. Wer dennoch dreißig Zeilen zeigen will, zeigt in Wahrheit ein Bild " +
          "von Daten – dann ist ein Diagramm ehrlicher.",
        "Der richtige Umgang mit einer großen Tabelle ist deshalb, sie zu verdichten: " +
          "die drei relevanten Zeilen zeigen, den Rest weglassen und die vollständige " +
          "Tabelle als Anlage mitgeben. Wenn die Zahlen wirklich alle gebraucht werden, " +
          "gehören sie in ein Dokument, nicht auf eine Folie.",
        "Eingefügt wird eine Tabelle wie jedes andere Objekt am besten über das " +
          "Tabellensymbol im leeren Inhaltsplatzhalter – dann sitzt sie im vorgesehenen " +
          "Bereich statt frei auf der Folie.",
      ],
      keyPoints: [
        "Richtwert: höchstens fünf Zeilen und vier Spalten.",
        "Tabellensymbol im leeren Inhaltsplatzhalter statt Einfügen → Tabelle.",
        "Große Datenmengen gehören in die Anlage, nicht auf die Folie.",
      ],
      task: {
        instruction: "Füge auf einer Folie eine Tabelle ein.",
        hint: "Am besten über das Tabellensymbol im leeren Inhaltsplatzhalter.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form (Tabelle, Diagramm, Bild)",
              "1.4",
              "Sieh selbst nach, ob auf einer Folie eine Tabelle liegt.",
            );
          }

          const tabellen = shapesOfType(snapshot, "Table");
          if (tabellen.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Tabelle eingefügt.",
              "Auf einer Folie mit dem Layout „Titel und Inhalt“ auf das Tabellensymbol im leeren Platzhalter klicken.",
            );
          }
          return pass(`Tabelle gefunden – auf Folie ${tabellen[0].slideIndex + 1}.`);
        },
      },
    },
    {
      id: "umfang-und-kopfzeile",
      title: "Umfang und Kopfzeile",
      explanation: [
        "Die erste Zeile einer Tabelle ist die Kopfzeile. Sie sagt, was in den Spalten " +
          "steht, und ist der Anker für alles Weitere: PowerPoint formatiert sie über die " +
          "Option „Kopfzeile“ unter „Tabellenentwurf“ hervorgehoben, und Screenreader " +
          "lesen sie beim Wechsel in eine Zelle mit vor.",
        "Deshalb muss sie vollständig gefüllt sein. Eine leere Zelle in der Kopfzeile " +
          "bedeutet für jemanden, der die Tabelle vorgelesen bekommt, eine Spalte ohne " +
          "Namen – die Zahl darin ist dann bedeutungslos.",
        "In den Zellen selbst steht so wenig wie möglich. Ganze Sätze in einer " +
          "Tabellenzelle sind ein sicheres Zeichen dafür, dass der Inhalt eigentlich in " +
          "eine Aufzählung gehört. Tabellen sind für Werte da, die sich vergleichen " +
          "lassen – Zahlen, Kurzbezeichnungen, Ja/Nein.",
      ],
      keyPoints: [
        "Tabellenentwurf → Tabellenformatoptionen → Kopfzeile.",
        "Jede Zelle der Kopfzeile muss gefüllt sein.",
        "Tab springt zur nächsten Zelle, Tab in der letzten Zelle hängt eine Zeile an.",
      ],
      task: {
        instruction:
          "Gib deiner Tabelle eine vollständig gefüllte Kopfzeile und halte sie bei " +
          "höchstens fünf Zeilen und vier Spalten. In den Zellen sollen keine ganzen " +
          "Sätze stehen.",
        hint: "Die Kopfzeile ist die erste Zeile – jede Zelle darin braucht einen Text.",
        check: async () => {
          const tabellen = await readTables();

          if (tabellen === null) {
            return nichtPruefbar(
              "Der Inhalt einer Tabelle",
              "1.8",
              "Prüfe selbst: erste Zeile vollständig gefüllt, höchstens fünf Zeilen und vier Spalten, keine ganzen Sätze in den Zellen.",
            );
          }
          if (tabellen.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Tabelle eingefügt.",
              "Füge zuerst über den Inhaltsplatzhalter eine Tabelle ein.",
            );
          }

          // Bewertet wird die Tabelle, die den Anforderungen am nächsten kommt.
          const bewertet = tabellen.map((tabelle) => {
            const langeZellen = verboseCells(tabelle);
            return {
              tabelle,
              kopfzeile: hasFilledHeaderRow(tabelle),
              umfang: tabelle.rowCount <= 5 && tabelle.columnCount <= 4,
              knapp: langeZellen.length === 0,
              langeZellen,
            };
          });
          const beste =
            bewertet.find((eintrag) => eintrag.kopfzeile && eintrag.umfang && eintrag.knapp) ??
            bewertet[0];
          const nummer = beste.tabelle.slideIndex + 1;

          return fromCriteria(
            [
              criterion(
                `Die Kopfzeile auf Folie ${nummer} ist vollständig gefüllt`,
                beste.kopfzeile,
              ),
              criterion(
                `Höchstens fünf Zeilen und vier Spalten (gefunden: ${beste.tabelle.rowCount} × ${beste.tabelle.columnCount})`,
                beste.umfang,
              ),
              criterion(
                beste.knapp
                  ? "Keine Zelle enthält einen ganzen Satz"
                  : `Zu viel Text in einer Zelle: „${beste.langeZellen[0].slice(0, 45)}“`,
                beste.knapp,
              ),
            ],
            {
              success: `Die Tabelle auf Folie ${nummer} ist auf einen Blick erfassbar.`,
              failure: `Die Tabelle auf Folie ${nummer} ist noch nicht folientauglich.`,
              hint: "Zeilen und Spalten löschen statt die Schrift zu verkleinern – und was nicht in eine Zelle passt, gehört in die Sprechernotizen.",
            },
          );
        },
      },
    },
    {
      id: "gestaltung",
      title: "Tabellen gestalten",
      explanation: [
        "Unter „Tabellenentwurf“ gibt es fertige Tabellenformatvorlagen. Sie folgen dem " +
          "Design der Präsentation, was der entscheidende Punkt ist: Wer die Zellen von " +
          "Hand einfärbt, hat nach einem Designwechsel eine Tabelle in den alten Farben.",
        "Die Tabellenformatoptionen daneben steuern, welche Bereiche hervorgehoben " +
          "werden: Kopfzeile, Ergebniszeile, erste und letzte Spalte, verbundene Zeilen. " +
          "Für eine Folientabelle reichen meist die Kopfzeile und verbundene Zeilen – " +
          "abwechselnd hinterlegte Zeilen helfen dem Auge, in der Zeile zu bleiben.",
        "Ein häufiger Wunsch ist, Zahlen rechtsbündig auszurichten. Das ist richtig und " +
          "wichtig: Nur rechtsbündige Zahlen lassen sich in der Größenordnung " +
          "vergleichen, weil die Einer, Zehner und Hunderter untereinanderstehen. Text " +
          "bleibt linksbündig.",
      ],
      keyPoints: [
        "Tabellenentwurf → Tabellenformatvorlagen folgen dem Design.",
        "Zahlen rechtsbündig, Text linksbündig.",
        "Zellen niemals von Hand einfärben – sonst überlebt es keinen Designwechsel.",
      ],
    },
  ],
};
